import { Box, Button, CircularProgress, Divider, Grid, Typography } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useAtom } from 'jotai';
import { RoutePaths } from '../../../routes/AppRoutes';
import { DraftProduct, draftProductAtom } from '../state';
import ProductFormQuitBtn from '../ProductFormQuitBtn';
import { useProductClassificationGenericProductsQuery } from '../../../queries/__generated__/productClassificationGenericProducts.generated';
import { useClassifyProductGenericProductQuery } from '../../../queries/__generated__/classifyProductGenericProduct.generated';
import ProductFormVisibilityFields from './ProductFormVisibilityFields';

export default function ProductFormVisibility(): React.JSX.Element {
  const { t } = useTranslation(['common', 'products']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [draftProduct, setDraftProduct] = useAtom(draftProductAtom);
  const search = `?${searchParams.toString()}`;
  const form = useForm<DraftProduct>({
    defaultValues: {
      ...draftProduct,
      generic_product_code: draftProduct?.generic_product_code,
      classification_tag_codes: draftProduct?.classification_tag_codes,
      classification_tags_none: draftProduct?.classification_tags_none || false,
    } as DraftProduct,
  });
  const {
    handleSubmit,
    watch,
    setValue,
    formState: {
      isDirty,
      isSubmitSuccessful,
    },
  } = form;
  const genericProductCode = watch('generic_product_code');
  const classificationTagCodes = watch('classification_tag_codes');
  const classificationTagsNone = watch('classification_tags_none');
  const hasTagsOrNone = !!classificationTagCodes?.length || !!classificationTagsNone;

  const { data: genericProductsData, loading: genericProductsLoading } = useProductClassificationGenericProductsQuery({
    variables: { subFamilyCode: draftProduct?.sub_family || '' },
    skip: !draftProduct?.sub_family,
  });

  const genericOptions = genericProductsData?.product_classification_node || [];
  const draftCode = draftProduct?.generic_product_code;
  const draftCodeMatchesFamily = !!draftCode && genericOptions.some((option) => option.code === draftCode);

  const { data: classifyData, loading: classifyingLoading } = useClassifyProductGenericProductQuery({
    variables: {
      name: draftProduct?.name || '',
      subFamily: draftProduct?.sub_family || '',
      brand: draftProduct?.brand,
      description: draftProduct?.description,
      labels: draftProduct?.labels,
    },
    skip: !draftProduct || draftCodeMatchesFamily,
  });
  const isClassifyingLoading = genericProductsLoading || classifyingLoading;

  useEffect(() => {
    if (!draftProduct) {
      navigate(RoutePaths.PRODUCT_ADD_INFO, { replace: true });
    }
  }, [draftProduct]);

  useEffect(() => {
    if (!genericProductsData) {
      return;
    }

    if (draftCodeMatchesFamily) {
      return;
    }

    const result = classifyData?.classifyProductGenericProduct;
    if (result?.status === 'classified' && result.genericProductCode) {
      setValue('generic_product_code', result.genericProductCode);
    }
  }, [genericProductsData, classifyData]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      navigate({
        pathname: RoutePaths.PRODUCT_ADD_SPECIFICATIONS,
        search,
      });
    }
  }, [isSubmitSuccessful]);

  const onSubmit = (data: DraftProduct): void => {
    setDraftProduct({
      ...data,
      classification_tag_codes: data.classification_tags_none ? [] : data.classification_tag_codes,
    });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {isClassifyingLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
            <CircularProgress />
            <Typography variant="bodyMedium">
              {t('products:classifying_product_nature')}
            </Typography>
          </Box>
        )}
        <Grid container rowSpacing={4}>
          {!isClassifyingLoading && (
            <ProductFormVisibilityFields />
          )}

          <Divider sx={{ width: '100%' }} />
          <Grid
            size={{ xs: 12 }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              gap: { xs: 1, sm: 2 },
            }}
          >
            <ProductFormQuitBtn productId={undefined} shouldBlock={isDirty && !isSubmitSuccessful} />
            <Button
              variant="outlined"
              component={Link}
              to={{
                pathname: RoutePaths.PRODUCT_ADD_INFO,
                search,
              }}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {t('common:previous')}
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={!genericProductCode || !hasTagsOrNone}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {t('common:next')}
            </Button>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
}
